// packageMatch.js — matching an appointment's treatment to a wallet package.
//
// Design rule (owner directive 2026-08-13): the wallet count must NEVER be
// corrupted. So matching is CONSERVATIVE — every level below is "clearly the
// same treatment"; anything ambiguous returns null (no deduction), exactly
// like the old exact-match behavior. We only widen what was silently missing:
// price prefixes ("$129 Premium..."), promo suffixes ("... — $129 Special"),
// case/spacing differences, and composite appointments ("A + B").
//
// Match levels (first level with any hit wins; FIFO by expiry within it):
//   L1: normalized name equality (treatment_type or package_name)
//   L2: one segment of a composite appointment ("A + B + C") equals the name
//   L3: the package name appears as a whole-word phrase inside the treatment,
//       but ONLY for specific names (2+ words or 10+ chars) — a generic
//       single word like "Facial" must never match "Back Facial".

// Normalize a treatment/package name for comparison.
export function normalizeTreatment(raw) {
  let s = String(raw || "").toLowerCase().trim();
  // strip a leading price tag: "$129 ", "$9.99/unit "
  s = s.replace(/^\$\s*\d+(\.\d+)?(\s*\/\s*\w+)?\s*/, "");
  // strip a trailing promo/price clause: "— $129 Special", "- special", "($99)"
  s = s.replace(/\s*[—–-]\s*[^—–-]*(\$\s*\d|special|promo|offer)[^—–-]*$/i, "");
  s = s.replace(/\s*\(\s*\$\s*\d[^)]*\)\s*$/, "");
  // drop the standalone words "special"/"promo" anywhere
  s = s.replace(/\b(special|promo)\b/g, " ");
  // unify separators and collapse whitespace
  s = s.replace(/\s+/g, " ").trim();
  return s;
}

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Split a composite appointment name into segments: "A + B, C" → [A, B, C].
function segmentsOf(normalizedTreat) {
  return normalizedTreat
    .split(/\s*[+,]\s*/)
    .map((x) => x.trim())
    .filter(Boolean);
}

// A package name is "specific" enough for containment matching (L3) only if
// it can't collide with a broader treatment by accident.
function isSpecificName(name) {
  return name.length >= 10 || name.split(" ").length >= 2;
}

// Find the package to deduct from. `packages` must be pre-filtered to
// active / non-credit / sessions-remaining, and pre-sorted FIFO by expiry.
// Returns a package or null. Never guesses: no confident match → null.
export function findPackageForTreatment(treatment, packages) {
  const treat = normalizeTreatment(treatment);
  if (!treat || !packages || packages.length === 0) return null;
  const segs = segmentsOf(treat);

  const nameOf = (p) => ({
    type: normalizeTreatment(p.treatment_type),
    name: normalizeTreatment(p.package_name),
  });

  // L1 — full equality
  for (const p of packages) {
    const n = nameOf(p);
    if ((n.type && n.type === treat) || (n.name && n.name === treat)) return p;
  }

  // L2 — composite appointment: one of its segments equals the package name
  if (segs.length > 1) {
    for (const p of packages) {
      const n = nameOf(p);
      if (segs.some((s) => (n.type && n.type === s) || (n.name && n.name === s))) return p;
    }
  }

  // L3 — specific package name contained as a whole-word phrase
  for (const p of packages) {
    const n = nameOf(p);
    for (const cand of [n.type, n.name]) {
      if (!cand || !isSpecificName(cand)) continue;
      const re = new RegExp(`(^|[^a-z0-9])${escapeRe(cand)}([^a-z0-9]|$)`);
      if (re.test(treat)) return p;
    }
  }

  return null;
}
